// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Configuration objects for each report type's loader animation.
 *
 * Each configuration defines step labels, timing constants, and step weights
 * that control how the loader animates. These are extracted from the original
 * loader implementations to enable reuse and easy addition of new report types.
 */

/**
 * Configuration for a specific report type's loader animation.
 */
interface ReportLoaderConfig {
  reportTypeId: string;
  stepLabels: readonly string[];
  stepWeights: readonly number[];
  timing: {
    networkSpeed: number;
    parsingSpeed: number;
    fixedOverheadMs?: number;
  };
}

/** Default step weights used by most reports */
const DEFAULT_STEP_WEIGHTS = [
  0.03, 0.1, 0.14, 0.15, 0.15, 0.15, 0.1, 0.18,
] as const;

/** Step weights used by exception reports */
const EXCEPTION_STEP_WEIGHTS = [
  0.05, 0.1, 0.15, 0.2, 0.15, 0.15, 0.1, 0.1,
] as const;

// ============================================================================
// Asset Report Configurations (inventory, transit, field, fulfillment)
// ============================================================================

export const assetReportConfigs = {
  inventory: {
    reportTypeId: 'asset-inventory',
    stepLabels: [
      'Accessing database',
      'Loading asset registry',
      'Scanning location records',
      'Calculating inventory periods',
      'Analyzing time in location',
      'Aggregating by location/product',
      'Building asset list',
      'Preparing report',
    ],
    stepWeights: DEFAULT_STEP_WEIGHTS,
    timing: {
      networkSpeed: 127000, // Asset-days per second
      parsingSpeed: 309000, // Asset-days per second
    },
  },
  transit: {
    reportTypeId: 'asset-transit',
    stepLabels: [
      'Accessing database',
      'Loading asset registry',
      'Scanning shipment records',
      'Tracking transit periods',
      'Calculating time in transit',
      'Identifying shipment routes',
      'Building asset list',
      'Preparing report',
    ],
    stepWeights: DEFAULT_STEP_WEIGHTS,
    timing: {
      networkSpeed: 127000,
      parsingSpeed: 309000,
    },
  },
  field: {
    reportTypeId: 'asset-field',
    stepLabels: [
      'Accessing database',
      'Loading asset registry',
      'Scanning deployment records',
      'Calculating field durations',
      'Analyzing time deployed',
      'Aggregating by location/product',
      'Building asset list',
      'Preparing report',
    ],
    stepWeights: DEFAULT_STEP_WEIGHTS,
    timing: {
      networkSpeed: 127000,
      parsingSpeed: 309000,
    },
  },
  fulfillment: {
    reportTypeId: 'asset-fulfillment',
    stepLabels: [
      'Accessing database',
      'Loading project registry',
      'Scanning assignments',
      'Calculating requested counts',
      'Comparing assigned counts',
      'Identifying fulfillment gaps',
      'Building project list',
      'Preparing report',
    ],
    stepWeights: DEFAULT_STEP_WEIGHTS,
    timing: {
      networkSpeed: 10000, // Assets per second (no date range)
      parsingSpeed: 3000,
    },
  },
} as const satisfies Record<string, ReportLoaderConfig>;

export type AssetReportType = keyof typeof assetReportConfigs;

// ============================================================================
// Snapshot Report Configurations (inventory, fixed, sold, scrapped)
// ============================================================================

export const snapshotReportConfigs = {
  inventory: {
    reportTypeId: 'snapshot-inventory',
    stepLabels: [
      'Accessing database',
      'Loading asset registry',
      'Scanning inventory dates',
      'Calculating time in inventory',
      'Analyzing cost basis',
      'Computing expected values',
      'Building asset list',
      'Preparing report',
    ],
    stepWeights: DEFAULT_STEP_WEIGHTS,
    timing: {
      networkSpeed: 11000,
      parsingSpeed: 1900,
    },
  },
  fixed: {
    reportTypeId: 'snapshot-fixed',
    stepLabels: [
      'Accessing database',
      'Loading asset registry',
      'Scanning in-service dates',
      'Calculating depreciation',
      'Analyzing book values',
      'Validating useful life',
      'Building asset list',
      'Preparing report',
    ],
    stepWeights: DEFAULT_STEP_WEIGHTS,
    timing: {
      networkSpeed: 7000,
      parsingSpeed: 1700,
    },
  },
  sold: {
    reportTypeId: 'snapshot-sold',
    stepLabels: [
      'Accessing database',
      'Loading asset registry',
      'Scanning sale records',
      'Analyzing cost basis',
      'Computing gain/loss',
      'Validating transactions',
      'Building asset list',
      'Preparing report',
    ],
    stepWeights: DEFAULT_STEP_WEIGHTS,
    timing: {
      networkSpeed: 7000,
      parsingSpeed: 1700,
    },
  },
  scrapped: {
    reportTypeId: 'snapshot-scrapped',
    stepLabels: [
      'Accessing database',
      'Loading asset registry',
      'Scanning disposal records',
      'Analyzing cost basis',
      'Computing gain/loss',
      'Validating transactions',
      'Building asset list',
      'Preparing report',
    ],
    stepWeights: DEFAULT_STEP_WEIGHTS,
    timing: {
      networkSpeed: 7000,
      parsingSpeed: 1700,
    },
  },
} as const satisfies Record<string, ReportLoaderConfig>;

export type SnapshotReportType = keyof typeof snapshotReportConfigs;

// ============================================================================
// Exception Report Configurations (date, detail)
// ============================================================================

export const exceptionReportConfigs = {
  date: {
    reportTypeId: 'exception-date',
    stepLabels: [
      'Accessing database',
      'Loading asset registry',
      'Scanning inventory dates',
      'Analyzing lifecycle events',
      'Checking date sequences',
      'Identifying exceptions',
      'Building exception list',
      'Preparing report',
    ],
    stepWeights: EXCEPTION_STEP_WEIGHTS,
    timing: {
      networkSpeed: 70000,
      parsingSpeed: 600000,
      fixedOverheadMs: 800,
    },
  },
  detail: {
    reportTypeId: 'exception-detail',
    stepLabels: [
      'Accessing database',
      'Loading asset registry',
      'Scanning cost basis data',
      'Validating useful life values',
      'Checking salvage percentages',
      'Analyzing deployment costs',
      'Identifying exceptions',
      'Preparing report',
    ],
    stepWeights: EXCEPTION_STEP_WEIGHTS,
    timing: {
      networkSpeed: 600000,
      parsingSpeed: 1200000,
      fixedOverheadMs: 800,
    },
  },
} as const satisfies Record<string, ReportLoaderConfig>;

export type ExceptionReportType = keyof typeof exceptionReportConfigs;

// ============================================================================
// Rollforward Report Configurations (inventory, fixed-assets)
// ============================================================================

export const rollforwardReportConfigs = {
  inventory: {
    reportTypeId: 'rollforward-inventory',
    stepLabels: [
      'Accessing database',
      'Loading asset registry',
      'Scanning inventory dates',
      'Calculating starting positions',
      'Tracking asset additions',
      'Analyzing deployments',
      'Processing dispositions',
      'Preparing rollforward',
    ],
    stepWeights: DEFAULT_STEP_WEIGHTS,
    timing: {
      networkSpeed: 9400,
      parsingSpeed: 22500,
    },
  },
  'fixed-assets': {
    reportTypeId: 'rollforward-fixed-assets',
    stepLabels: [
      'Accessing database',
      'Loading asset registry',
      'Scanning in-service dates',
      'Calculating starting positions',
      'Tracking asset additions',
      'Computing depreciation',
      'Processing dispositions',
      'Preparing rollforward',
    ],
    stepWeights: DEFAULT_STEP_WEIGHTS,
    timing: {
      networkSpeed: 9400,
      parsingSpeed: 22500,
    },
  },
} as const satisfies Record<string, ReportLoaderConfig>;

export type RollforwardReportType = keyof typeof rollforwardReportConfigs;

// ============================================================================
// Bridge Report Configurations (inventory, fixed-assets)
// ============================================================================

export const bridgeReportConfigs = {
  inventory: {
    reportTypeId: 'bridge-inventory',
    stepLabels: [
      'Accessing database',
      'Loading asset registry',
      'Scanning inventory periods',
      'Tracking asset additions',
      'Analyzing deployments',
      'Processing dispositions',
      'Aggregating by period',
      'Preparing bridge data',
    ],
    stepWeights: DEFAULT_STEP_WEIGHTS,
    timing: {
      // Per-rollforward timing - multiplied by periodCount via useAssetPeriods
      networkSpeed: 20000,
      parsingSpeed: 40000,
    },
  },
  'fixed-assets': {
    reportTypeId: 'bridge-fixed-assets',
    stepLabels: [
      'Accessing database',
      'Loading asset registry',
      'Scanning in-service dates',
      'Tracking asset additions',
      'Computing depreciation',
      'Processing dispositions',
      'Aggregating by period',
      'Preparing bridge data',
    ],
    stepWeights: DEFAULT_STEP_WEIGHTS,
    timing: {
      // Per-rollforward timing - multiplied by periodCount via useAssetPeriods
      networkSpeed: 20000,
      parsingSpeed: 40000,
    },
  },
} as const satisfies Record<string, ReportLoaderConfig>;

export type BridgeReportType = keyof typeof bridgeReportConfigs;

// ============================================================================
// Depreciation Schedule Report Configuration
// ============================================================================

export const depreciationScheduleReportConfig = {
  reportTypeId: 'depreciation-schedule',
  stepLabels: [
    'Accessing database',
    'Loading fixed assets',
    'Scanning in-service dates',
    'Applying filters',
    'Building monthly periods',
    'Computing depreciation',
    'Calculating book values',
    'Preparing schedule',
  ],
  stepWeights: DEFAULT_STEP_WEIGHTS,
  timing: {
    // Based on observed 33k-asset depreciation schedule runs:
    // 3 months: 4.03s, 6 months: 4.47s, 12 months: 6.3s, 24 months: 8.53s.
    // This uses a conservative 3.3s fixed overhead plus about 130k
    // asset-periods/sec across request and parse work.
    networkSpeed: 260000,
    parsingSpeed: 260000,
    fixedOverheadMs: 3300,
  },
} as const satisfies ReportLoaderConfig;

// ============================================================================
// Close Run Configuration (accounting period close)
// ============================================================================

export const closeRunConfig = {
  reportTypeId: 'close-run',
  stepLabels: [
    'Initializing close run',
    'Loading asset registry',
    'Calculating inventory rollforward',
    'Computing depreciation',
    'Generating fixed asset rollforward',
    'Processing prior period adjustments',
    'Creating journal entries',
    'Finalizing close run',
  ],
  stepWeights: [0.05, 0.1, 0.2, 0.2, 0.2, 0.1, 0.1, 0.05] as const,
  timing: {
    // Close run processes 2,400-3,600 assets per second
    // Using the midpoint (3,000) for network speed
    // and the lower bound (2,400) for parsing speed
    networkSpeed: 3000,
    parsingSpeed: 2400,
  },
} as const satisfies ReportLoaderConfig;
